import { UserPermissonResponse, getInfograhpicUsers } from 'libs/e1api/permissionmanager';
import { getTeams, getUserPermissions } from 'libs/e1api/workspace';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { TeamsResponse, UserPermissionResponse } from '../workspace.types';

const WORKSPACE_TEAMS_KEY = '/workspace/teams';
const WORKSPACE_PERMISSIONS_KEY = '/workspace/getUserPermissions';
const SWR_CONFIG = {
  revalidateIfStale: false,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
};

interface useWorkspaceReturn {
  teams?: TeamsResponse['teams'];
  permissions?: UserPermissionResponse['permissions'];
  userPermissions?: UserPermissonResponse['permissions'];
  revalidate?: () => Promise<boolean>;
}

/**
 * Fetch the team and permission data regarding the enterprise workspace
 *
 * @param e1InfographId
 * @param enabled
 * @returns
 */
export const useWorkspace = (e1InfographId?: string, enabled: boolean = false): useWorkspaceReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { data: teamsData, mutate: mutateGetTeams } = useSWR(
    enabled ? WORKSPACE_TEAMS_KEY : null,
    getTeams,
    SWR_CONFIG,
  );
  const { data: permissionsData, mutate: mutateGetUserPermissions } = useSWR(
    enabled ? WORKSPACE_PERMISSIONS_KEY : null,
    getUserPermissions,
    SWR_CONFIG,
  );
  const { data: userPermissionsData, mutate: mutateGetInfographicUsers } = useSWR(
    enabled ? e1InfographId : null,
    getInfograhpicUsers,
    SWR_CONFIG,
  );

  const filteredTeams = useMemo(
    () =>
      teamsData &&
      teamsData?.teams
        .filter((team) => team.isMember) // only show teams that the user is a member of
        .filter((team, index, self) => index === self.findIndex((t) => t.team_name === team.team_name)) // remove duplicate teams
        .filter((team) => !userPermissionsData?.permissions?.groups?.some((group) => group.team_id === team.id)), // remove teams that the user has already shared with
    [teamsData, userPermissionsData],
  );

  useEffect(() => {
    if (teamsData && permissionsData && userPermissionsData) {
      setIsLoaded(true);
    }
  }, [teamsData, permissionsData, userPermissionsData]);

  const result = useMemo(() => {
    if (!enabled || !isLoaded) return { teams: undefined, permissions: undefined, userPermissions: undefined };
    return {
      teams: filteredTeams,
      permissions: permissionsData?.permissions,
      userPermissions: userPermissionsData?.permissions,
      revalidate: async () => {
        await Promise.all([mutateGetTeams(), mutateGetUserPermissions(), mutateGetInfographicUsers()]);
        return true;
      },
    };
  }, [
    enabled,
    isLoaded,
    filteredTeams,
    permissionsData,
    userPermissionsData,
    mutateGetTeams,
    mutateGetUserPermissions,
    mutateGetInfographicUsers,
  ]);

  return result;
};
